import posthog from "posthog-js";
import { posthogKey, posthogHost, debug } from "./constants";

// PostHog analytics.
//
// This app is embedded as a cross-origin iframe in the Webflow site, which has
// two consequences the config below deals with:
//
//   1. Third party cookies are blocked in most browsers, so persistence uses
//      localStorage only. If even that is unavailable (Safari in an iframe
//      without prior interaction), posthog-js falls back to memory on its own —
//      events still arrive, they just aren't tied together across page loads.
//   2. `$current_url` / `$referrer` describe the iframe, not the page the user
//      is actually looking at. `embedded` and `embed_parent_url` are registered
//      as super properties so every event carries the parent page too.
//
// Sign up and booking forms contain personal data of (often underage)
// applicants, so no field *values* are ever sent — only ids, types and counts.
// Secret URL tokens are stripped from every URL property, see sanitizeUrl().

let enabled = false;

// Route params that must not reach analytics: the booking token and the payment
// entry uid are effectively access credentials. The activity id is a plain
// public id and is kept, since it is what makes the numbers useful.
const secretPathPatterns = [
  [/\/rezervacija\/[^/?#]+/gi, "/rezervacija/:token"],
  [/\/placilo\/[^/?#]+/gi, "/placilo/:uid"],
];

// Query params the embedding page uses to hand us its identity and its own URL,
// see getBootstrap() and getParentUrl(). They are noise in URL properties, so
// they get stripped.
const bridgeParams = ["ph_distinct_id", "ph_session_id", "ph_parent_url"];

const stripBridgeParams = (value) => {
  const queryStart = value.indexOf("?");
  if (queryStart === -1) return value;

  const hashStart = value.indexOf("#", queryStart);
  const query = value.slice(
    queryStart + 1,
    hashStart === -1 ? undefined : hashStart
  );

  const kept = query
    .split("&")
    .filter((part) => part && bridgeParams.indexOf(part.split("=")[0]) === -1);

  return (
    value.slice(0, queryStart) +
    (kept.length ? `?${kept.join("&")}` : "") +
    (hashStart === -1 ? "" : value.slice(hashStart))
  );
};

const sanitizeUrl = (value) => {
  if (typeof value !== "string") return value;
  let result = stripBridgeParams(value);
  for (let [pattern, replacement] of secretPathPatterns)
    result = result.replace(pattern, replacement);
  return result;
};

const urlProperties = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$referring_domain",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
  "$session_entry_url",
  "$session_entry_pathname",
  "$session_entry_referrer",
  "embed_parent_url",
];

const sanitizeProperties = (properties) => {
  for (let key of urlProperties)
    if (properties[key]) properties[key] = sanitizeUrl(properties[key]);
  return properties;
};

// The URL of the page the iframe is embedded in.
//
// The referrer is only a last resort: browsers default to a Referrer-Policy of
// strict-origin-when-cross-origin, so for a cross-origin iframe it is truncated
// to the bare origin ("https://404.si/") and cannot tell you which page the form
// was embedded on. The embedding page therefore passes its own URL explicitly.
const getParentUrl = () => {
  const passed = new URLSearchParams(window.location.search).get("ph_parent_url");
  if (passed) return passed.split("#")[0];

  try {
    if (window.parent !== window && window.parent.location.href)
      return window.parent.location.href.split("#")[0];
  } catch (e) {
    // cross-origin parent, expected
  }
  if (document.referrer) return document.referrer.split("#")[0];
  return null;
};

const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // access to window.top throws when cross-origin, which means we are embedded
    return true;
  }
};

// Campaign attribution lives on the *embedding* page's URL, never on ours: a Meta
// ad links to 404.si/...?utm_source=facebook&fbclid=..., and the iframe src
// carries none of that. PostHog reads these off its own document, so form
// submissions would have no attribution at all.
//
// Lifting them off the parent URL and registering them as super properties puts
// them on signup_form_submitted itself, so campaigns can be compared by breaking
// the conversion event down by utm_source. Keeping them as *event* properties is
// deliberate — the alternative is person properties like $initial_utm_source,
// which only exist once a person profile is created, and creating one for these
// forms would mean holding personal data of applicants who are often minors.
const campaignParams = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  // stable id for the campaign: ad and campaign *names* get edited over time,
  // which silently splits historical reporting, ids do not
  "utm_id",
  // click ids, useful for reconciling against the ad platforms themselves
  "fbclid",
  "gclid",
  "msclkid",
  "ttclid",
];

const getCampaignProperties = (parentUrl) => {
  const searches = [];

  if (parentUrl) {
    const queryStart = parentUrl.indexOf("?");
    if (queryStart !== -1) searches.push(parentUrl.slice(queryStart));
  }
  // when opened directly rather than embedded, our own URL is the landing page
  searches.push(window.location.search);

  const properties = {};
  for (let search of searches) {
    const params = new URLSearchParams(search);
    for (let key of campaignParams) {
      const value = params.get(key);
      if (!properties[key] && value) properties[key] = value;
    }
  }
  return properties;
};

// A cross-origin iframe gets its own storage partition, so by default we would
// generate a fresh distinct id and session id and the embedding page's events
// would never join up with ours. When the embedding page runs PostHog too it can
// hand us its ids as query params on the iframe src, and we continue that same
// person and session instead of starting new ones. See README for the snippet
// the Webflow side needs. Without those params this returns {} and we behave as
// a standalone site.
const getBootstrap = () => {
  const params = new URLSearchParams(window.location.search);
  const distinctID = params.get("ph_distinct_id");
  const sessionID = params.get("ph_session_id");

  const bootstrap = {};
  if (distinctID) bootstrap.distinctID = distinctID;
  // must be the parent's session id, which posthog-js generates as a UUID v7
  if (sessionID) bootstrap.sessionID = sessionID;
  return bootstrap;
};

export const initAnalytics = () => {
  if (!posthogKey) return;

  const bootstrap = getBootstrap();

  posthog.init(posthogKey, {
    bootstrap: bootstrap,
    api_host: posthogHost,
    // opts into the SPA-aware pageview default, so react-router navigations
    // are captured as pageviews
    defaults: "2025-05-24",
    capture_pageview: "history_change",
    // no third party cookies in an iframe
    persistence: "localStorage",
    // forms hold personal data, keep replays off unless deliberately enabled
    disable_session_recording: true,
    sanitize_properties: sanitizeProperties,
    debug: debug,
  });

  const parentUrl = getParentUrl();

  posthog.register({
    embedded: isEmbedded(),
    embed_parent_url: parentUrl,
    // handy for spotting a broken handoff: embedded but not bridged means the
    // iframe is being counted as its own visitor
    embed_bridged: !!bootstrap.distinctID,
    ...getCampaignProperties(parentUrl),
  });

  enabled = true;
};

export const track = (event, properties) => {
  if (!enabled) return;
  posthog.capture(event, properties);
};

// Pulls a comparable status out of an axios error without logging response
// bodies, which may echo submitted personal data back at us.
export const errorProperties = (reason) => ({
  error_status: reason && reason.response ? reason.response.status : null,
  error_message: reason && reason.message ? reason.message : null,
});

export default posthog;
